import React, { useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Iridescence from './Iridescence';
import './LandingPage.css';
import DeveloperCredit from './DeveloperCredit';
import HelportLogo from './HelportLogo';

const BackgroundAnimation = memo(() => (
  <div className="landing-bg-layer">
    <Iridescence
      color={[0.08627450980392157, 0.8, 0.7490196078431373]}
      mouseReact={true}
      amplitude={0.1}
      speed={1}
    />
    <div className="landing-grid-overlay" aria-hidden="true" />
    <div className="landing-glow landing-glow--left" aria-hidden="true" />
    <div className="landing-glow landing-glow--right" aria-hidden="true" />
  </div>
));

const FeatureIcon = ({ type }) => {
  if (type === 'shield') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    );
  }
  if (type === 'clock') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    );
  }
  if (type === 'qr') {
    return (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <path d="M14 14h3v3h-3zM17 17h3v3h-3zM14 20h3" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
    </svg>
  );
};

const CardHelportLogo = () => (
  <div className="card-helport-logo">
    <HelportLogo variant="icon" />
  </div>
);

const LandingPage = ({
  handleAdminAuth,
  adminCredential,
  setAdminCredential,
  adminPass,
  setAdminPass,
  onOpenTerminal,
}) => {
  const [showLogin, setShowLogin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="landing-container">
      <BackgroundAnimation />

      <div className="landing-content">
        <AnimatePresence mode="wait">
          {!showLogin ? (
            <motion.div
              key="hero-view"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
              className="animated-border-wrapper"
            >
              <div className="hero-inner-box">
                <div className="landing-left">
                  <div className="branding-block">
                    <HelportLogo variant="full" showTagline onDark />
                  </div>

                  <div className="landing-badge">
                    <span className="landing-badge-dot" />
                    RFID + QR Workforce System
                  </div>

                  <h1 className="landing-headline">
                    <span className="landing-headline-accent">Seamless</span> Attendance.
                    <br />
                    <span className="landing-headline-gradient">Future-Proof</span> Security.
                  </h1>

                  <p className="landing-desc">
                    Enterprise-grade time tracking with RFID cards, QR backup scans, and real-time Google Sheets sync — built for Helport workforce operations.
                  </p>

                  <div className="landing-stats">
                    <div className="landing-stat">
                      <span className="landing-stat-value">RFID</span>
                      <span className="landing-stat-label">Tap &amp; Go</span>
                    </div>
                    <div className="landing-stat-divider" aria-hidden="true" />
                    <div className="landing-stat">
                      <span className="landing-stat-value">QR</span>
                      <span className="landing-stat-label">Backup Scan</span>
                    </div>
                    <div className="landing-stat-divider" aria-hidden="true" />
                    <div className="landing-stat">
                      <span className="landing-stat-value">Live</span>
                      <span className="landing-stat-label">Sheet Sync</span>
                    </div>
                  </div>

                  <div className="landing-features">
                    <span className="feature-pill"><FeatureIcon type="shield" /> Secure Access</span>
                    <span className="feature-pill"><FeatureIcon type="clock" /> Real-time Logs</span>
                    <span className="feature-pill"><FeatureIcon type="bolt" /> Instant Scan</span>
                    <span className="feature-pill"><FeatureIcon type="qr" /> QR Backup</span>
                  </div>

                  <div className="landing-cta-row">
                    <motion.button
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      type="button"
                      className="terminal-btn"
                      onClick={onOpenTerminal}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                        <circle cx="12" cy="13" r="4" />
                      </svg>
                      Open Attendance Terminal
                    </motion.button>

                    <motion.button
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      type="button"
                      className="admin-login-btn admin-login-btn--outline"
                      onClick={() => setShowLogin(true)}
                    >
                      Admin / Portal Login
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <line x1="5" y1="12" x2="19" y2="12" />
                        <polyline points="12 5 19 12 12 19" />
                      </svg>
                    </motion.button>
                  </div>

                  <DeveloperCredit variant="landing" />
                </div>

                <div className="landing-right">
                  <div className="nfc-pulse-ring nfc-pulse-ring--1" aria-hidden="true" />
                  <div className="nfc-pulse-ring nfc-pulse-ring--2" aria-hidden="true" />
                  <div className="nfc-pulse-ring nfc-pulse-ring--3" aria-hidden="true" />

                  <motion.div
                    animate={{ y: [0, -12, 0], rotate: [-8, -6, -8] }}
                    transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                    className="rfid-card card-back"
                  >
                    <div className="card-top-row">
                      <div className="chip" />
                      <CardHelportLogo />
                    </div>
                    <div className="card-bottom">
                      <div className="card-num">**** 2001</div>
                      <div className="card-label">Employee ID</div>
                    </div>
                  </motion.div>

                  <motion.div
                    animate={{ y: [0, 12, 0], rotate: [6, 8, 6] }}
                    transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut' }}
                    className="rfid-card card-front"
                  >
                    <div className="scan-line" />
                    <div className="card-top-row">
                      <div className="chip" />
                      <CardHelportLogo />
                    </div>
                    <div className="card-bottom">
                      <div className="card-num">HELPORT RFID</div>
                      <div className="card-label">Tap to Clock In / Out</div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="login-view"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="login-overlay"
            >
              <div className="login-border-animated">
                <div className="login-form-card">
                  <HelportLogo variant="form" showTagline onDark />
                  <h3>Secure Access</h3>
                  <p className="login-form-subtitle">
                    Admin, Team Leader, or Employee portal account
                  </p>

                  <form onSubmit={handleAdminAuth} className="login-form">
                    <input
                      type="text"
                      placeholder="Username or EID"
                      value={adminCredential}
                      onChange={(e) => setAdminCredential(e.target.value)}
                      className="login-input login-input--pill"
                      autoComplete="username"
                      required
                    />
                    <div className="login-password-wrap">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Password"
                        value={adminPass}
                        onChange={(e) => setAdminPass(e.target.value)}
                        className="login-input login-input--pill login-input--with-icon"
                        autoComplete="current-password"
                        required
                      />
                      <button
                        type="button"
                        className="login-password-toggle"
                        onClick={() => setShowPassword((prev) => !prev)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        tabIndex={-1}
                      >
                        {showPassword ? (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                            <line x1="1" y1="1" x2="23" y2="23" />
                            <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        )}
                      </button>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      className="btn-signin"
                    >
                      Sign In
                    </motion.button>
                  </form>

                  <button type="button" className="btn-back-scanner" onClick={() => setShowLogin(false)}>
                    ← Back to Home
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <footer className="landing-footer">
        <span>© {new Date().getFullYear()} Helport · RFID Attendance System</span>
      </footer>
    </div>
  );
};

export default LandingPage;
